import React, { useEffect, useState, useRef } from 'react';
import PropTypes from "prop-types";
import withRouter from 'Common/withRouter';
import { Collapse } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';

import { withTranslation } from "react-i18next";

// Import Data
import navdata from "../LayoutMenuData";
import { Link } from 'react-router-dom';
import SidebarShimmer from '../../components/SidebarShimmer';

// Import sidebar styles
import './index.css';

const VerticalLayout = (props: any) => {
    const path = props.router.location.pathname;
    // Safely get navData once
    const navData = (navdata() && navdata().props && navdata().props.children) ? navdata().props.children : [];
    const [openMenus, setOpenMenus] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(props.loading || false);
    const [openSubMenus, setOpenSubMenus] = useState<string[]>([]);
    const lastClickedParentRef = useRef<string | null>(null);

    function toggleMenu(id: string) {
        setOpenMenus((prev) => {
            const isOpen = prev.includes(id);

            if (isOpen) {
                // If closing a menu, just close it
                lastClickedParentRef.current = null; // Reset when closing
                return prev.filter((mid) => mid !== id);
            } else {
                // Close all other parent modules and open only the current one
                // This provides better UX by keeping only one parent module open at a time
                
                // Clear sub-menus when opening a new parent to prevent auto-activation
                setOpenSubMenus([]);
                
                // Set the ref to track which parent was clicked
                lastClickedParentRef.current = id;
                
                return [id];
            }
        });

        // Handle auto-navigation for parent menus that have sub-items
        // Exclude 'tournaments' as it should only show sub-modules without auto-navigation
        const parentMenu = navData.find((item: any) => item.id === id);
        if (parentMenu && parentMenu.subItems && parentMenu.subItems.length > 0) {
            if (id !== 'tournaments') {
                // Find the first sub-item that has a link
                const firstChildWithLink = parentMenu.subItems.find((subItem: any) => subItem.link);
                if (firstChildWithLink) {
                    // Navigate to the first child's link
                    props.router.navigate(firstChildWithLink.link);
                }
            }
        }
    }

    function toggleSubMenu(id: string) {
        setOpenSubMenus((prev) => {
            const isOpen = prev.includes(id);
            
            // Define the mutually exclusive sub-modules
            const mutuallyExclusiveSubMenus = ['tournament-registrations', 'tournament-management'];
            
            if (isOpen) {
                // Close this submenu
                return prev.filter((mid) => mid !== id);
            } else {
                // Check if this is one of the mutually exclusive sub-menus
                if (mutuallyExclusiveSubMenus.includes(id)) {
                    // Close other mutually exclusive sub-menus and open this one
                    const filteredPrev = prev.filter((mid) => !mutuallyExclusiveSubMenus.includes(mid));
                    return [...filteredPrev, id];
                } else {
                    // For other sub-menus, keep the current behavior (open alongside others)
                    return [...prev, id];
                }
            }
        });
    }

    // Function to handle parent sub-module click with auto-navigation to first child
    function handleParentSubModuleClick(subItem: any) {
        // First toggle the submenu (this handles mutual exclusion)
        toggleSubMenu(subItem.id);
        
        // Auto-navigate to first child for all sub-modules that have children
        if (subItem.subItems && subItem.subItems.length > 0) {
            const firstChild = subItem.subItems[0];
            if (firstChild.link) {
                // Navigate to the first child's link
                props.router.navigate(firstChild.link);
            }
        }
    }

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const initMenu = () => {
            const pathName = process.env.PUBLIC_URL + path;
            const ul: any = document.getElementById("navbar-nav");
            const items = ul.getElementsByTagName("a");
            let itemsArray = [...items]; // converts NodeList to Array
            // removeActivation(itemsArray); // Removed manual class manipulation
            let matchingMenuItem = itemsArray.find((x) => {
                return pathName.startsWith(x.pathname);
            });
            if (matchingMenuItem) {
                // activateParentDropdown(matchingMenuItem); // Removed manual class manipulation
            }
        };
        if (props.layoutType === "vertical") {
            initMenu();
        }
    }, [path, props.layoutType]);

    // Update loading state when prop changes
    useEffect(() => {
        setIsLoading(props.loading || false);
    }, [props.loading]);

    // Simulate loading for shimmer effect (only if no external loading prop)
    useEffect(() => {
        if (!props.loading) {
            const timer = setTimeout(() => {
                setIsLoading(false);
            }, 1500); // Show shimmer for 1.5 seconds

            return () => clearTimeout(timer);
        }
    }, [props.loading]);

    // Remove all manual class manipulation and DOM logic
    // Only use NavLink for active state

    // Helper function to check if a menu item's link is a prefix of the current path
    function isMenuItemActive(item: any, currentPath: string): boolean {
        if (!item.link) {
            // If no link, check if any sub-item is active
            if (item.subItems) {
                return item.subItems.some((sub: any) => isMenuItemActive(sub, currentPath));
            }
            return false;
        }

        // Exact match for the main link
        if (currentPath === item.link) {
            return true;
        }

        // If the item has no sub-items, check for prefix match
        if (!item.subItems) {
            return currentPath.startsWith(item.link + '/');
        }

        // If there are sub-items, check if any sub-item is active
        return item.subItems.some((sub: any) => isMenuItemActive(sub, currentPath));
    }

    // Add a helper to check if any subItem matches the current path (recursive for nested sub-modules)
    function isSubItemActive(subItems: any[], path: string): boolean {
        return subItems.some(subItem => {
            // First check if this subItem itself is active
            if (subItem.link && path.startsWith(subItem.link)) {
                return true;
            }
            
            // Special case for tournament details: if we're on a tournament detail page
            if (subItem.id === "tournaments" && path.match(/^\/tournament-details\/[^\/]+$/)) {
                return true;
            }
            // Special case for blog details: if on add/edit blog, mark 'Blogs' as active
            if (subItem.id === "blogs" && path.match(/^\/blog-details\/blogs(\/add|\/[\w-]+)?$/)) {
                return true;
            }
            
            // If this subItem has its own subItems, check recursively
            if (subItem.subItems && subItem.subItems.length > 0) {
                return isSubItemActive(subItem.subItems, path);
            }
            
            return false;
        });
    }

    // This effect runs when the path changes to open the correct parent menu.
    useEffect(() => {
        // Check if we just clicked on a parent module that doesn't auto-navigate
        // If so, skip automatic activation to prevent unwanted sub-menu opening
        if (lastClickedParentRef.current === 'tournaments') {
            lastClickedParentRef.current = null; // Reset the ref
            return; // Skip automatic activation
        }

        // First, check if we're on a simple menu item (like dashboard)
        const activeSimpleMenu = navData.find((item: any) => 
            !item.subItems && item.link && path === item.link
        );

        if (activeSimpleMenu) {
            // For simple menu items like dashboard, no need to open any submenus
            return;
        }

        // Then check for submenu items
        const activeParentMenu = navData.find((item: any) =>
            item.subItems && isSubItemActive(item.subItems, path)
        );

        if (activeParentMenu) {
            setOpenMenus((prev) => {
                // Only open the active parent menu if it's not already open
                // This prevents overriding user interactions
                if (!prev.includes(activeParentMenu.id)) {
                    return [activeParentMenu.id];
                }
                return prev;
            });
            
            // Also ensure the sub-menu is open if we're on a sub-module
            const activeSubMenu = activeParentMenu.subItems.find((subItem: any) => 
                isMenuItemActive(subItem, path)
            );
            
            if (activeSubMenu && activeSubMenu.subItems && activeSubMenu.subItems.length > 0) {
                setOpenSubMenus((prev) => {
                    if (!prev.includes(activeSubMenu.id)) {
                        // Define the mutually exclusive sub-modules
                        const mutuallyExclusiveSubMenus = ['tournament-registrations', 'tournament-management'];
                        
                        // Check if this is one of the mutually exclusive sub-menus
                        if (mutuallyExclusiveSubMenus.includes(activeSubMenu.id)) {
                            // Close other mutually exclusive sub-menus and open this one
                            const filteredPrev = prev.filter((mid) => !mutuallyExclusiveSubMenus.includes(mid));
                            return [...filteredPrev, activeSubMenu.id];
        } else {
                            // For other sub-menus, keep the current behavior
                            return [...prev, activeSubMenu.id];
                        }
                    }
                    return prev;
                });
            }
        }
    }, [path]);




    // Recursive component for rendering nested sub-modules
    const renderSubMenu = (subItems: any[], level: number = 1) => {
        return subItems.map((subItem: any, subKey: number) => {
            const hasSubItems = subItem.subItems && subItem.subItems.length > 0;
            const isSubMenuOpen = openSubMenus.includes(subItem.id);
            const isActive = isMenuItemActive(subItem, path);
            
            // Check if a child is active (only if we're actually on a child page)
            const hasActiveChild = hasSubItems && subItem.subItems.some((child: any) => isMenuItemActive(child, path));
            
            // For parent sub-modules that should auto-navigate to first child, consider them active when they are open
            // This provides better UX when user clicks on a parent sub-module
            // But only if they are mutually exclusive and no other mutually exclusive parent is open
            const mutuallyExclusiveSubMenus = ['tournament-registrations', 'tournament-management'];
            const isMutuallyExclusive = mutuallyExclusiveSubMenus.includes(subItem.id);
            const shouldAutoNavigate = hasSubItems; // All sub-modules with children should auto-navigate
            const otherMutuallyExclusiveOpen = isMutuallyExclusive && 
                openSubMenus.some(openId => 
                    mutuallyExclusiveSubMenus.includes(openId) && openId !== subItem.id
                );
            
            const isParentActive = hasSubItems && shouldAutoNavigate && isSubMenuOpen && !hasActiveChild && 
                (!isMutuallyExclusive || !otherMutuallyExclusiveOpen);
            
            if (hasSubItems) {
                return (
                    <li className={`sidebar-submenu-item${level > 1 ? ' nested-level-' + level : ''}`} key={subKey}>
                        <div
                            className={`nav-link sidebar-submenu-link${isActive || isParentActive ? ' sidebar-submenu-active' : ''}${hasActiveChild && !isActive && !isParentActive ? ' has-active-child' : ''}`}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleParentSubModuleClick(subItem);
                            }}
                            style={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                paddingLeft: level === 1 ? '16px' : `${16 + (level - 1) * 12}px`,
                                paddingRight: '16px',
                                paddingTop: '12px',
                                paddingBottom: '12px',
                                margin: '0',
                                borderRadius: '12px',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {subItem.icon && <i className={subItem.icon} style={{ marginRight: '12px', fontSize: '16px' }} />}
                            <span className="submenu-label">{props.t(subItem.label)}</span>
                            <span className={`submenu-arrow ${isSubMenuOpen ? 'open' : ''}`} style={{ marginLeft: 'auto' }}>
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9.5 4.5L6 8L2.5 4.5" stroke="#C2C2C2" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </span>
                        </div>
                        <Collapse in={isSubMenuOpen} className="menu-dropdown">
                            <ul className="sidebar-submenu" style={{ width: '100%', margin: 0, padding: 0 }}>
                                {renderSubMenu(subItem.subItems, level + 1)}
                            </ul>
                        </Collapse>
                    </li>
                );
            } else {
                return (
                    <li className={`sidebar-submenu-item${level > 1 ? ' nested-level-' + level : ''}`} key={subKey}>
                        <NavLink
                            to={subItem.link}
                            end
                            className={({ isActive }) => {
                                if (isMenuItemActive(subItem, path)) {
                                    return "nav-link sidebar-submenu-active";
                                }
                                return "nav-link";
                            }}
                            style={{
                                paddingLeft: level === 1 ? '16px' : `${16 + (level - 1) * 12}px`,
                                paddingRight: '16px',
                                paddingTop: '12px',
                                paddingBottom: '12px',
                                margin: '0',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                transition: 'all 0.2s ease'
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                // Ensure parent menus stay open when navigating to sub-modules
                                const parentMenu = navData.find((item: any) => 
                                    item.subItems && item.subItems.some((sub: any) => 
                                        sub.id === subItem.id || (sub.subItems && sub.subItems.some((nested: any) => nested.id === subItem.id))
                                    )
                                );
                                if (parentMenu) {
                                    setOpenMenus((prev) => {
                                        if (!prev.includes(parentMenu.id)) {
                                            return [...prev, parentMenu.id];
                }
                return prev;
            });
        }
                            }}
                        >
                            {subItem.icon && <i className={subItem.icon} style={{ marginRight: '12px', fontSize: '16px' }} />}
                            <span className="submenu-label">{props.t(subItem.label)}</span>
                        </NavLink>
                    </li>
                );
            }
        });
    };

    // Shimmer skeleton components
    const ShimmerMenuHeader = () => (
        <li className="menu-title">
            <div className="shimmer-menu-header"></div>
        </li>
    );

    const ShimmerMenuItem = () => (
        <li className="nav-item">
            <div className="shimmer-menu-item"></div>
        </li>
    );

    const ShimmerMenuWithSubmenu = () => (
        <li className="nav-item has-submenu">
            <div className="shimmer-menu-with-submenu"></div>
            <div className="shimmer-submenu">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="shimmer-submenu-item"></div>
                ))}
            </div>
        </li>
    );

    // Loading skeleton
    if (isLoading) {
        return (
            <React.Fragment>
                <ShimmerMenuHeader />
                <ShimmerMenuItem />
                <ShimmerMenuWithSubmenu />
                <ShimmerMenuItem />
                <ShimmerMenuWithSubmenu />
                <ShimmerMenuItem />
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            {/* Show shimmer loader when loading */}
            {isLoading && <SidebarShimmer items={8} />}
            
            {/* menu Items */}
            {!isLoading && (navData || []).map((item: any, key: number) => {
                // Parent is active only if a submenu is selected
                const subItemActive = item.subItems ? isSubItemActive(item.subItems, path) : false;
                const isOpen = openMenus.includes(item.id);
                const parentActive = subItemActive; // Only highlight parent if a submenu is selected

                return (
                    <React.Fragment key={key}>
                        {/* Main Header */}
                        {item['isHeader'] ? (
                            <li className="menu-title">
                                <span data-key="t-menu">{props.t(item.label)} </span>
                            </li>
                        ) : (
                            item.subItems ? (
                                <li className={`nav-item has-submenu${parentActive ? ' parent-active' : ''}${isOpen ? ' open' : ''}`}>
                                    <div
                                        className={`nav-link menu-link has-submenu`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            toggleMenu(item.id);
                                        }}
                                        style={{
                                            cursor: 'pointer',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'stretch',
                                            padding: 0,
                                            position: 'relative'
                                        }}
                                    >
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: '100%',
                                            padding: '14px 16px'
                                        }}>
                                            {item.icon && <i className={item.icon} style={{ marginRight: 8 }} />}
                                            <span className="menu-label">{props.t(item.label)}</span>
                                            <span className={`submenu-arrow ${isOpen ? 'open' : ''}`} style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M9.5 4.5L6 8L2.5 4.5" stroke="#C2C2C2" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </span>
                                        </div>
                                        <Collapse in={isOpen} className="menu-dropdown">
                                            <ul className="sidebar-submenu" style={{ width: '100%', margin: 0, padding: 0 }}>
                                                {renderSubMenu(item.subItems)}
                                            </ul>
                                        </Collapse>
                                    </div>
                                </li>
                            ) : (
                                <li className="nav-item">
                                    <NavLink
                                        to={item.link ? item.link : "/"}
                                        end
                                        className={isMenuItemActive(item, path) ? "nav-link menu-link active" : "nav-link menu-link"}
                                        onClick={() => {
                                            // Close all other open menus when clicking on a simple menu item
                                            setOpenMenus([]);
                                            setOpenSubMenus([]);
                                        }}
                                    >
                                        {item.icon && <i className={item.icon} style={{ marginRight: 8 }} />}
                                        <span className="menu-label">{props.t(item.label)}</span>
                                    </NavLink>
                                </li>
                            )
                        )}
                    </React.Fragment>
                );
            })}
        </React.Fragment>
    );
};

VerticalLayout.propTypes = {
    location: PropTypes.object,
    t: PropTypes.any,
    loading: PropTypes.bool,
};

export default withRouter(withTranslation()(VerticalLayout));